import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePrompt } from '@/components/admin/PromptDialog';
import { Wallet, AlertTriangle, HandCoins, CheckCircle2, Ban, CalendarClock } from 'lucide-react';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';

// S134 (2/N) — Cobranza. El tablero del dinero que entra y del que sale.
//
// Tres bloques, en el orden en que se miran de verdad:
//
//   1. Lo cobrado del mes: la única cifra que se ve a diario.
//   2. La cartera: lo sano (todavía no vence) y lo vencido, partido por
//      antigüedad, porque no es lo mismo una semana que cuatro meses.
//   3. Las comisiones: lo que se le debe a socios y vendedores, y el botón
//      para autorizar y registrar el pago.
//
// Cerrar el mes, aprobar y pagar son TRES botones distintos a propósito: son
// tres decisiones y tres momentos. Juntarlas en un clic es como se paga doble.

const TARJETA = 'border rounded-lg p-4 bg-white';

function Cifra({ titulo, valor, sub, alarma }) {
  return (
    <div className={`${TARJETA} ${alarma ? 'border-rose-200 bg-rose-50/60' : ''}`}>
      <div className="text-sm text-slate-500">{titulo}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1">{valor}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Ayuda({ children }) {
  return (
    <div className="border-l-4 border-slate-300 bg-slate-50 pl-3 py-2 text-sm text-slate-700">
      {children}
    </div>
  );
}

export default function Cobranza() {
  const { ask, ui: promptUi } = usePrompt();
  const [cargando, setCargando] = useState(true);
  const [datos, setDatos] = useState(null);
  const [politica, setPolitica] = useState(null);
  const [periodo, setPeriodo] = useState(() => {
    // Por defecto, el mes pasado: es el que ya se puede cerrar.
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [tablero, pol] = await Promise.all([
        api.get('/admin/cobranza'),
        api.get('/admin/cobranza/politica-de-churn'),
      ]);
      setDatos(tablero.data);
      setPolitica(pol.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function cerrarMes() {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(periodo)) {
      toast.error('El mes se escribe como 2026-08.');
      return;
    }
    setOcupado(true);
    try {
      const { data } = await api.post('/admin/cobranza/cerrar-mes', { periodo });
      toast.success(`Mes cerrado: ${data.liquidaciones.length} liquidación(es). `
                    + `Se pagan a partir del ${data.pagar_desde}.`);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function aprobar(id) {
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/cobranza/liquidaciones/${id}/aprobar`);
      toast.success(data.aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function pagar(liq) {
    const sugerida = `pago comision ${(liq.beneficiario || 'socio').trim()} ${(liq.periodo || periodo || '').trim()}`
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
    const referencia = await ask({
      title: `Pagar ${liq.neto} ${liq.moneda} a ${liq.beneficiario}`,
      description: 'Sugerencia de referencia (puedes editarla) para identificarlo en el banco:',
      defaultValue: sugerida,
      placeholder: 'Referencia (transferencia, folio)',
    });
    if (!referencia) return;
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/cobranza/liquidaciones/${liq.id}/pagar`,
                                      { referencia });
      toast.success(data.aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function anular(liq) {
    const motivo = await ask({
      title: 'Anular liquidación',
      description: 'Devuelve sus comisiones a «pendientes de liquidar». No borra nada.\n¿Por qué se anula?',
      multiline: true,
    });
    if (!motivo) return;
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/cobranza/liquidaciones/${liq.id}/anular`,
                                      { motivo });
      toast.success(data.aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  if (cargando) return <div className="p-6 text-slate-500">Cargando la cobranza…</div>;

  const sana = datos?.cartera_sana || {};
  const vencida = datos?.cartera_vencida || {};
  const comisiones = datos?.comisiones || {};
  const cobrado = datos?.cobrado_del_mes?.por_moneda || [];

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {promptUi}
      <div className="flex items-center gap-2">
        <Wallet className="w-6 h-6 text-emerald-600" />
        <h1 className="text-2xl font-semibold">Cobranza</h1>
      </div>

      <SectionHelp
        module="cobranza"
        title="esta pantalla"
        description={'El tablero del dinero de la plataforma: lo que ya entró este '
          + 'mes, lo que falta por entrar y lo que hay que pagarle a socios y a '
          + 'vendedores. Las comisiones se cierran por mes y se pagan a mes '
          + 'vencido, así que aquí se decide, no se improvisa.'}
        whatItDoesNot={'no le cobra a nadie ni suspende ninguna cuenta —eso es la '
          + 'pantalla de Cobros— y no borra nada: una comisión mal hecha se '
          + 'corrige con un cargo en contra, nunca borrando el original.'} />

      <Ayuda>
        <div>
          Aquí se ve el dinero: lo que entró, lo que falta por entrar y lo que hay que
          pagarle a socios y vendedores. <b>Esta pantalla no cobra ni suspende a nadie</b> —
          eso vive en Cobros. Aquí se mira para decidir.
        </div>
        <div className="mt-1">
          <b>Cartera sana</b> es lo que está registrado y todavía no vence: dinero
          esperado, no dinero en riesgo. <b>Cartera vencida</b> es lo que ya pasó de
          fecha, partido por antigüedad porque no es lo mismo una semana de atraso
          que cuatro meses.
        </div>
      </Ayuda>

      {/* ── 1. Lo cobrado del mes ── */}
      <section className="space-y-2">
        <h2 className="font-medium flex items-center gap-2">
          Cobrado este mes
          <HelpTooltip text={'Lo que de verdad entró a la cuenta en el mes en curso, separado por moneda. No incluye lo facturado que todavía no se ha pagado.'} />
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cobrado.length === 0 && (
            <div className={`${TARJETA} col-span-2 text-sm text-slate-600`}>
              Todavía no ha entrado ningún cobro este mes.
            </div>
          )}
          {cobrado.map((c) => (
            <Cifra key={c.moneda} titulo={`Cobrado (${c.moneda})`}
                   valor={c.total} sub={`${c.cobros} cobro(s)`} />
          ))}
          <Cifra titulo="Cartera sana" valor={sana.total ?? '0'}
                 sub={`${sana.cobros ?? 0} cobro(s) que aún no vencen`} />
          <Cifra titulo="Cartera vencida" valor={vencida.total ?? '0'}
                 sub={`${vencida.cobros ?? 0} cobro(s) pasados de fecha`}
                 alarma={(vencida.cobros ?? 0) > 0} />
        </div>
        <p className="text-xs text-slate-500">
          Las cuentas Corp (nuestras marcas) no cuentan aquí: cobrarse a uno mismo
          no es ingreso.
        </p>
      </section>

      {/* ── 2. Cartera vencida por antigüedad ── */}
      <section className="space-y-2">
        <h2 className="font-medium flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Cartera vencida por antigüedad
          <HelpTooltip text={'Lo que ya pasó de fecha, partido por cuánto lleva atrasado. No es lo mismo una semana que cuatro meses: entre más viejo, menos probable es que se recupere.'} />
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(vencida.por_antiguedad || []).map((t) => (
            <Cifra key={t.clave} titulo={t.etiqueta} valor={t.total}
                   sub={`${t.cobros} cobro(s)`} alarma={t.clave === 'mas_90' && t.cobros > 0} />
          ))}
        </div>
        {(vencida.detalle || []).length > 0 && (
          <div className="border rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Negocio</th>
                  <th className="text-left px-3 py-2">Concepto</th>
                  <th className="text-right px-3 py-2">Monto</th>
                  <th className="text-right px-3 py-2">Días</th>
                  <th className="text-left px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {vencida.detalle.map((r) => (
                  <tr key={r.pago_id} className="border-t">
                    <td className="px-3 py-2">
                      {r.negocio}
                      {r.suspendido && (
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">
                          suspendido
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{r.concepto}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.monto} {r.moneda}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{r.dias_vencido}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {r.cobranza === 'al_corriente' ? 'Sin proceso de cobranza' : r.cobranza}
                      {r.intentos > 0 && ` · ${r.intentos} intento(s)`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(vencida.detalle || []).length === 0 && (
          <div className={`${TARJETA} text-sm text-slate-600`}>
            Nadie está vencido. Toda la cartera está sana.
          </div>
        )}
      </section>

      {/* ── 3. Comisiones ── */}
      <section className="space-y-3">
        <h2 className="font-medium flex items-center gap-2">
          <HandCoins className="w-4 h-4 text-indigo-600" /> Comisiones a pagar
          <HelpTooltip text={'Lo que le debemos a socios reseller y a vendedores de casa. Se devenga con cada pago del cliente y se paga a mes vencido; si el cliente se cae pronto, parte se recupera con un cargo en contra.'} />
        </h2>

        <Ayuda>
          <div><b>Se paga a mes vencido.</b> Lo devengado en agosto se liquida a partir
          del 1 de septiembre, nunca antes.</div>
          {/* Sin optional chaining aquí la pantalla revienta entera si la
              política no llega — lo cazó el guardia de render. Que falte un
              texto de ayuda no puede tumbar el tablero del dinero. */}
          {(politica?.tramos || []).length > 0 && (
            <div className="mt-1">
              <b>Si un cliente se cae:</b>{' '}
              {politica.tramos.map((t) => t.texto).join(' ')}
            </div>
          )}
          {politica?.tasas_de_casa && (
            <div className="mt-1">
              <b>Vendedor de casa:</b> {politica.tasas_de_casa.primera_venta}% de la primera
              venta y {politica.tasas_de_casa.meses_siguientes}% de cada mes siguiente.{' '}
              {politica.tasas_de_casa.nota}
            </div>
          )}
        </Ayuda>

        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-slate-500 mb-1 flex items-center gap-1">
              Mes a cerrar
              <HelpTooltip text={'El mes cuyas comisiones se van a congelar en una cifra por beneficiario. Solo se puede cerrar un mes que ya terminó, y cerrarlo no paga nada: después hay que aprobar y registrar el pago.'} />
            </label>
            <Input value={periodo} onChange={(e) => setPeriodo(e.target.value)}
                   placeholder="2026-08" className="w-32" />
          </div>
          <Button onClick={cerrarMes} disabled={ocupado}>
            <CalendarClock className="w-4 h-4 mr-1" /> Cerrar el mes
          </Button>
          <span className="text-xs text-slate-500 pb-2">
            Arma la cifra de cada beneficiario. Un mes que no ha terminado no se puede cerrar.
          </span>
        </div>

        {(comisiones.devengado_sin_cerrar || []).length > 0 && (
          <div className="space-y-1">
            <h3 className="text-sm font-medium flex items-center gap-2">
          Devengado, todavía sin cerrar
          <HelpTooltip text={'Comisiones que ya se ganaron pero cuyo mes no se ha cerrado. Todavía pueden moverse: no se le debe nada a nadie hasta que el mes se cierre.'} />
        </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {comisiones.devengado_sin_cerrar.map((d, i) => (
                <div key={i} className={`${TARJETA} py-2`}>
                  <div className="text-sm font-medium">{d.beneficiario}</div>
                  <div className="text-xs text-slate-500">
                    {d.tipo === 'reseller' ? 'Socio reseller' : 'Vendedor de casa'}
                  </div>
                  <div className="text-lg tabular-nums">{d.total} {d.moneda}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1">
          <h3 className="text-sm font-medium flex items-center gap-2">
          Liquidaciones
          <HelpTooltip text={'La cifra final de cada beneficiario por mes, ya cerrada. Se aprueba, se paga y se deja la referencia. Anular una devuelve sus comisiones a pendientes, no las borra.'} />
        </h3>
          {(comisiones.liquidaciones_pendientes || []).length === 0 ? (
            <div className={`${TARJETA} text-sm text-slate-600`}>
              No hay liquidaciones pendientes. Cierra un mes para armarlas.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Beneficiario</th>
                    <th className="text-left px-3 py-2">Mes</th>
                    <th className="text-right px-3 py-2">Bruto</th>
                    <th className="text-right px-3 py-2">Recuperado</th>
                    <th className="text-right px-3 py-2">A pagar</th>
                    <th className="text-left px-3 py-2">Se paga desde</th>
                    <th className="text-right px-3 py-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {comisiones.liquidaciones_pendientes.map((l) => (
                    <tr key={l.id} className="border-t">
                      <td className="px-3 py-2">
                        {l.beneficiario}
                        <div className="text-xs text-slate-500">
                          {l.tipo === 'reseller' ? 'Socio reseller' : 'Vendedor de casa'}
                        </div>
                      </td>
                      <td className="px-3 py-2">{l.periodo}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{l.bruto}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${Number(l.recuperado) < 0 ? 'text-rose-700' : ''}`}>
                        {l.recuperado}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {l.neto} {l.moneda}
                      </td>
                      <td className="px-3 py-2">
                        {l.pagar_desde}
                        {!l.ya_se_puede_pagar && (
                          <div className="text-xs text-amber-700">todavía no</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {l.estado === 'open' && (
                          <Button size="sm" variant="outline" disabled={ocupado}
                                  onClick={() => aprobar(l.id)}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Aprobar
                          </Button>
                        )}
                        {l.estado === 'approved' && (
                          <Button size="sm" disabled={ocupado || !l.ya_se_puede_pagar}
                                  onClick={() => pagar(l)}>
                            Registrar pago
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" disabled={ocupado}
                                onClick={() => anular(l)}>
                          <Ban className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* ── 4. Licencias de socios de marca blanca ── */}
      {(datos?.licencias_de_socios || []).length > 0 && (
        <section className="space-y-2">
          <h2 className="font-medium flex items-center gap-2">
          Licencias por cobrar a socios de marca blanca
          <HelpTooltip text={'Lo que NOSOTROS le facturamos al socio que vende con su propia marca: sus licencias activas o su mínimo pactado, lo que sea mayor.'} />
        </h2>
          <div className="border rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Socio</th>
                  <th className="text-left px-3 py-2">Mes</th>
                  <th className="text-right px-3 py-2">Activas</th>
                  <th className="text-right px-3 py-2">Mínimo</th>
                  <th className="text-right px-3 py-2">Se le cobran</th>
                  <th className="text-right px-3 py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {datos.licencias_de_socios.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2">{c.socio}</td>
                    <td className="px-3 py-2">{c.periodo}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.activas}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.minimo}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{c.cobradas}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.total} {c.moneda}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">
            Se cobran las activas o el mínimo pactado, lo que sea mayor. Las tres cifras
            se guardan por separado para que el socio pueda ver por qué se le cobró eso.
          </p>
        </section>
      )}
    </div>
  );
}
